import { hc } from "hono/client";
import type { AppType } from "@/server/app";
import { adminSession } from "./session.svelte";

type EnhancedError = Error & { code?: string; requestId?: string };
type Attribution = {
  pageUrl: string;
  referrer?: string;
  fbclid?: string;
  fbp?: string;
  fbc?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
};

const browserOrigin =
  typeof window === "undefined" ? "http://127.0.0.1:24269" : window.location.origin;
export const API_BASE = browserOrigin;

/** Shown when a response carries no message: never expose transport details to visitors. */
const GENERIC_FAILURE = "We couldn't complete that request. Please try again.";
const OFFLINE_FAILURE = "We couldn't reach Bibow. Please check your connection and try again.";

/** Turns raw fetch failures (offline, DNS, aborted) into a message fit for a visitor. */
const friendlyFetch: typeof fetch = async (input, init) => {
  try {
    return await fetch(input, init);
  } catch {
    throw new Error(OFFLINE_FAILURE);
  }
};

export const api = hc<AppType>(API_BASE.endsWith("/") ? API_BASE : `${API_BASE}/`, {
  fetch: friendlyFetch,
});

export type ApiFailure = {
  success?: false;
  error?: { code?: string; message?: string; requestId?: string; details?: unknown };
};

function attachApiErrorMetadata(error: EnhancedError, failure: ApiFailure | null | undefined) {
  const code = failure?.error?.code;
  const requestId = failure?.error?.requestId;
  if (code !== undefined) error.code = code;
  if (requestId !== undefined) error.requestId = requestId;
}

export async function unwrap<T = unknown>(response: Response): Promise<T> {
  const payload = (await response.json().catch(() => null)) as
    | (T & { success?: boolean })
    | ApiFailure
    | null;
  if (!response.ok || !payload || payload.success === false) {
    const failure = payload as ApiFailure | null;
    const error = new Error(failure?.error?.message || GENERIC_FAILURE) as EnhancedError;
    attachApiErrorMetadata(error, failure);
    if (response.status === 401 && adminSession.authenticated) adminSession.clear();
    throw error;
  }
  if (
    typeof payload === "object" &&
    payload &&
    "success" in payload &&
    payload.success === true &&
    "data" in payload
  ) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

/**
 * Auth now rides on the better-auth session cookie, so calls carry no header.
 * Kept so existing call sites stay unchanged.
 */
export function adminHeaders(): Record<string, string> {
  return {};
}

function safeDecodeCookie(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function putIfPresent(
  target: Attribution,
  key: keyof Omit<Attribution, "pageUrl">,
  value: string | null | undefined,
) {
  const normalized = value?.trim();
  if (normalized) target[key] = normalized;
}

export function currentAttribution(): Attribution {
  if (typeof window === "undefined" || typeof document === "undefined") return { pageUrl: "" };

  const params = new URLSearchParams(window.location.search);
  const cookies: Record<string, string> = {};
  for (const part of document.cookie.split(";")) {
    const [rawKey, ...rest] = part.trim().split("=");
    const key = rawKey?.trim();
    if (!key) continue;
    cookies[key] = safeDecodeCookie(rest.join("="));
  }

  const attribution: Attribution = { pageUrl: window.location.href };
  putIfPresent(attribution, "referrer", document.referrer);
  putIfPresent(attribution, "fbclid", params.get("fbclid"));
  putIfPresent(attribution, "fbp", cookies._fbp);
  putIfPresent(attribution, "fbc", cookies._fbc);
  putIfPresent(attribution, "utmSource", params.get("utm_source"));
  putIfPresent(attribution, "utmMedium", params.get("utm_medium"));
  putIfPresent(attribution, "utmCampaign", params.get("utm_campaign"));
  putIfPresent(attribution, "utmContent", params.get("utm_content"));
  putIfPresent(attribution, "utmTerm", params.get("utm_term"));
  return attribution;
}

export async function unwrapEnvelope<T = unknown, M = unknown>(
  response: Response,
): Promise<{ data: T; meta?: M }> {
  const payload = (await response.json().catch(() => null)) as {
    success?: boolean;
    data?: T;
    meta?: M;
    error?: { code?: string; message?: string; requestId?: string };
  } | null;
  if (!response.ok || !payload || payload.success === false) {
    const error = new Error(payload?.error?.message || GENERIC_FAILURE) as EnhancedError;
    attachApiErrorMetadata(error, payload as ApiFailure | null);
    if (response.status === 401 && adminSession.authenticated) adminSession.clear();
    throw error;
  }
  if (!("data" in payload)) throw new Error(GENERIC_FAILURE);
  const result: { data: T; meta?: M } = { data: payload.data as T };
  if (payload.meta !== undefined) result.meta = payload.meta;
  return result;
}
